import { SITE_SIZES } from '../data/wizardOptions';

/**
 * Determines the matching SITE_SIZES object for a given area in hectares.
 *
 * @param {number} area - Area in hectares
 * @returns {Object} Matching SITE_SIZES object (small, medium, large, enterprise)
 */
export const getSiteSizeCategory = (area = 0) => {
  const numericArea = Number(area) || 0;

  // Sort descending by minSize to match the highest applicable bucket
  const sortedSizes = [...SITE_SIZES].sort((a, b) => b.minSize - a.minSize);
  return sortedSizes.find((s) => numericArea >= s.minSize) || SITE_SIZES[0];
};

/**
 * Translates a numeric hectare value (or total summed area) into a localized size range string.
 *
 * @param {number} area - Area in hectares
 * @param {Function} [t] - Translation function from useI18n
 * @returns {string} Localized range label (e.g., "1 - 5 Hectares")
 */
export const formatSiteSize = (area, t) => {
  const category = getSiteSizeCategory(area);

  if (t) {
    return t(`wizard.sizes.${category.id}`);
  }

  // Fallback labels if translation function is not provided
  const fallbacks = {
    small: '< 1 Hectare',
    medium: '1 - 5 Hectares',
    large: '5 - 20 Hectares',
    enterprise: '20+ Hectares'
  };

  return fallbacks[category.id] || '< 1 Hectare';
};

/**
 * Calculates total area across an array of plots/sites.
 *
 * @param {Array} plots - Array of plot/site objects
 * @returns {number} Sum of size values in hectares
 */
export const calculateTotalSiteArea = (plots = []) => {
  if (!Array.isArray(plots) || plots.length === 0) return 0;

  return plots.reduce((sum, plot) => {
    const plotSize = plot.size ?? plot.area ?? plot.areaHectares ?? plot.totalAreaHectares ?? 0;
    return sum + (Number(plotSize) || 0);
  }, 0);
};
